import {
  type OutletResource,
  OutletsController_list_v1,
  type TerminalResource,
  TerminalsController_list_v1,
  type TicketResource,
  TicketsController_list_v1,
  type UserResource,
  UsersController_list_v1,
} from '@repo/api-client';
import { Badge } from '@repo/ui/components/badge';
import { Label } from '@repo/ui/components/label';
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@repo/ui/components/select';
import { Switch } from '@repo/ui/components/switch';
import { TableBody, TableCell, TableHead, TableHeader, TableRow } from '@repo/ui/components/table';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router';

import { useApiClient } from '@/api/api-client';
import { collectPages } from '@/api/collect-pages';
import { useSession } from '@/auth/session';
import { formatPaise } from '@/lib/money';

import { DayPicker } from '../back-office/DayPicker';
import { Pager } from '../back-office/Pager';
import { type PageState, ROWS_PER_PAGE } from '../back-office/pagination';
import {
  EmptyState,
  LoadFailed,
  Screen,
  ScreenFact,
  ScreenTable,
  TableNotice,
  TableSkeleton,
} from '../back-office/Screen';
import { ticketPath } from '../paths';
import {
  BUSINESS_DATE_MALFORMED,
  formatBusinessDate,
  fromCalendarDate,
  isCalendarDate,
  toCalendarDate,
} from './business-date';
import { labelForItemKind, TICKET_ITEM_KINDS, type TicketItemKind } from './ticket-kinds';
import { describeRecordingGap, formatInstant } from './ticket-time';
import { isVoided, NO_VOID_REASON_GIVEN, readVoidReason } from './ticket-void';
import { TicketLookup } from './TicketLookup';

/**
 * A day's tickets: what the gates and the outlets issued, and the filters that
 * narrow it to the question somebody is actually asking.
 *
 * ## Voided tickets are shown by default, and that is not the catalogue's rule
 * ## copied wrong
 *
 * `includeVoided` **defaults to `true`** on `GET /v1/tickets`, which is the
 * opposite of `includeRetired` on the catalogue list, and FR-333 says so
 * deliberately. Do not tidy the two into agreement — this comment is here so the
 * next person does not.
 *
 * They look like the same option and are not. A retired catalogue item is not
 * sellable, so hiding it by default answers the question the back office is
 * asking there: *what can we sell*. A voided ticket is part of the day's record.
 * Money was taken and then given back, and a list that dropped it by default
 * would show a day whose takings do not add up with **nothing on the screen to
 * explain why** — which is the worst kind of wrong number, because it looks
 * right. So the switch in the rail is "Hide voided tickets", off; "what did we
 * actually sell today" is a fair second question, and it is the one you ask for.
 *
 * ## The list is paged, and the visible set is the server's to decide
 *
 * `docs/ui-conventions.md`, "Reading a list", asks for the strategy to be picked
 * deliberately. This is case 2 and emphatically so: tickets grow with *use*,
 * every day, for ever — `collect-pages.ts` names sales and tickets in as many
 * words as the thing it is the wrong answer for. `TicketReadsService.list` is
 * asserted to cost a fixed number of statements at any volume, proved at 10 rows
 * and at 500 (R-310, SC-307), so paging is both correct and cheap and a busy day
 * is not a reason to fear Next.
 *
 * **The outlets, terminals and people beside it are read whole**, from this same
 * file, which is the same rule applied to different sets rather than an
 * inconsistency: they fill filters that need every option at once, and the park
 * has a dozen outlets, tens of devices and tens of staff.
 *
 * What a caller may see is **bounded in the query**, not here (FR-322, R-109):
 * an owner and a manager reach every outlet, outlet and gate staff reach their
 * assignments, and `outletScopeFilter` is a conjunct of the same `WHERE` the
 * filters below become. So nothing on this screen filters rows client-side and
 * nothing assumes a page is the whole set — the count in the rail is the
 * server's `total`, and there is deliberately no takings sum anywhere on the
 * screen, because a sum of the rows on one page is a number that looks like a
 * day's takings and is not one.
 *
 * ## The number is the way in, and it is above the list
 *
 * The filters answer "what did we sell"; the field above the table answers "what
 * is *this* one", which is the question a visitor at the counter brings. It is
 * here rather than on a screen of its own because this is where a manager
 * already is — see `TicketLookup.tsx` — and each row's number follows through to
 * the same record, so the two ways of arriving at a ticket land in one place.
 *
 * ## No "today", on purpose
 *
 * The day filter opens empty and the list arrives newest-first by the server's
 * clock. Defaulting it to today would mean working out what today *is* in
 * Asia/Kolkata from the machine's clock, and the machine's clock is the one
 * thing this app refuses to read (`lib/clock.ts`) — on a terminal it is
 * explicitly not trusted to order anything (FR-008). The server's own ordering
 * is a better answer to "what has happened today" than the device's opinion of
 * what today is, and picking a day is one keystroke away.
 */

/** Every state the rows can be in. One value, so two cannot coexist. */
type ListState =
  | { readonly kind: 'loading' }
  | { readonly kind: 'loaded'; readonly tickets: readonly TicketResource[] }
  | { readonly kind: 'failed'; readonly error: unknown };

/** The value a picker carries when it is not filtering. */
const ANY = 'ALL';

/**
 * The columns that are always there — ticket, when, kind, amount. Outlet and
 * "Issued by" are conditional and counted where they are decided.
 *
 * Kept next to the header cells below, so the two cannot drift.
 */
const FIXED_COLUMNS = 4;

export function Tickets() {
  const client = useApiClient();
  const { session } = useSession();

  // Two flags with the same test and the same answer today, deliberately **not**
  // folded into one. They are two different controllers, and a screen that read
  // the second off the first would go quietly wrong the day either decorator
  // moves.
  //
  // `TerminalsController` carries `@RequireRole('OWNER')` on the class, so the
  // device directory is the owner's. Without it the terminal filter has nothing
  // to offer — a ticket carries a `terminalId` and no device name, and a picker
  // of bare UUIDs is not a filter.
  const mayListTerminals = session?.user.role === 'OWNER';

  // `UsersController` carries `@RequireRole('OWNER')` on every operation and
  // CL-104 is explicit that the manager role does not manage people. The staff
  // directory is what turns a ticket's `actorId` into a name, so without it
  // there is no "Issued by" filter and no "Issued by" column.
  //
  // **This is a gap between the ticket and the API, not a decision taken here.**
  // FR-322 admits a manager to every outlet's tickets, and nothing admits a
  // manager to the two directories that name a device or a person — so a manager
  // gets the day, the outlet, the kind and the date, and an owner gets those plus
  // the two the directories carry. Reported in the PR body; it is an API change
  // and not something to cast around, because a UUID rendered where a name
  // belongs typechecks perfectly.
  const mayListPeople = session?.user.role === 'OWNER';

  const [outletId, setOutletId] = useState<string>(ANY);
  const [terminalId, setTerminalId] = useState<string>(ANY);
  const [actorId, setActorId] = useState<string>(ANY);
  const [kind, setKind] = useState<TicketItemKind | typeof ANY>(ANY);

  // What is in the day field, which is not the same as what is filtered by: a
  // value that names no real day is held here and never sent. See
  // `business-date.ts` — `2026-02-31` reaches PostgreSQL as a bound `::date`,
  // fails when it is parsed, and escapes as a *retryable* internal error, so the
  // browser is the right place for it to stop.
  const [day, setDay] = useState('');

  const [hideVoided, setHideVoided] = useState(false);
  const [offset, setOffset] = useState(0);

  const [state, setState] = useState<ListState>({ kind: 'loading' });

  // The window the last successful read answered with, kept **outside**
  // `ListState` on purpose: it survives the next load, so the pager and the
  // count stay where they are while a page is in flight instead of vanishing and
  // coming back. `null` until the first answer, because a pager reading "0
  // tickets" before anything has been asked is worse than no pager.
  const [page, setPage] = useState<PageState | null>(null);

  const [outlets, setOutlets] = useState<readonly OutletResource[]>([]);
  const [terminals, setTerminals] = useState<readonly TerminalResource[]>([]);
  const [people, setPeople] = useState<readonly UserResource[]>([]);

  // Bumping this re-runs the load. A counter rather than a boolean, so two
  // retries in a row are two distinct values and the second one still fires.
  const [reloads, setReloads] = useState(0);

  const reload = useCallback(() => setReloads((count) => count + 1), []);

  // Any filter moving puts the list back at the first page. Left alone, a filter
  // applied at offset 200 lands on a page past the end of a shorter result and
  // reads as "nothing matched" — `pageAfter` clamps a *step*, and this is not
  // one.
  const changeFilter = useCallback((apply: () => void) => {
    apply();
    setOffset(0);
  }, []);

  // Empty means "not filtering by day", which is the field's own empty state and
  // not an error. A value that is filled in and names no real day filters
  // nothing and says so under the field.
  const dayIsReal = day === '' || isCalendarDate(day);
  const businessDate = day !== '' && dayIsReal ? day : '';

  useEffect(() => {
    // Two guards, not one. `controller` stops the request itself the moment a
    // filter moves, and `live` stops a response that was already in flight from
    // being written into state after this effect was torn down — without it,
    // changing a filter twice quickly can land the first answer on top of the
    // second and leave the screen showing rows the filters exclude.
    const controller = new AbortController();
    let live = true;

    setState({ kind: 'loading' });

    client
      .request(TicketsController_list_v1, {
        query: {
          offset,
          limit: ROWS_PER_PAGE,

          // Each filter is sent only when it is filtering. The query string is
          // what appears in a log when somebody is working out what a screen
          // asked for, and a `kind=ALL` in one is a filter that does not exist.
          ...(outletId === ANY ? {} : { outletId }),
          ...(terminalId === ANY ? {} : { terminalId }),
          ...(actorId === ANY ? {} : { actorId }),
          ...(kind === ANY ? {} : { kind }),
          ...(businessDate === '' ? {} : { businessDate }),

          // The one filter sent to turn something **off**. `includeVoided`
          // defaults to `true` at the API and the default is the requirement
          // (FR-333) — see the head of this file. Sending `true` explicitly
          // would restate the server's default in a place that would then have
          // to be changed if it ever moved.
          ...(hideVoided ? { includeVoided: false } : {}),
        },
        signal: controller.signal,
      })
      .then((answer) => {
        if (live) {
          setState({ kind: 'loaded', tickets: answer.items });

          // The window the *server* answered with, not the one that was asked
          // for. They are the same today, and a pager that assumed so would be
          // silently wrong the day the API clamps a limit.
          setPage({ offset: answer.offset, limit: answer.limit, total: answer.total });
        }
      })
      .catch((error: unknown) => {
        if (live) setState({ kind: 'failed', error });
      });

    return () => {
      live = false;
      controller.abort();
    };
  }, [client, outletId, terminalId, actorId, kind, businessDate, hideVoided, offset, reloads]);

  useEffect(() => {
    const controller = new AbortController();
    let live = true;

    // Read whole: a dozen outlets fill a filter that needs every option at once.
    // `GET /v1/outlets` is `@RequireRole()` + `@RequireOutletScope()`, so a
    // manager gets theirs and this list is already bounded by the same reach the
    // tickets are.
    //
    // A failure here is deliberately not the screen's failure — the day is still
    // readable without the outlet filter, so this leaves the list alone and the
    // filter empty rather than replacing a working screen with an error about a
    // control.
    collectPages<OutletResource>((from, limit) =>
      client.request(OutletsController_list_v1, {
        query: { offset: from, limit },
        signal: controller.signal,
      }),
    )
      .then((all) => {
        if (live) setOutlets(all);
      })
      .catch(() => {
        if (live) setOutlets([]);
      });

    return () => {
      live = false;
      controller.abort();
    };
  }, [client]);

  useEffect(() => {
    // Not asked for at all where the API would refuse it: a request that can
    // only 403 is not a request worth making, and the control it fills is not
    // rendered either. The role came out of `sessionStorage` and is editable, so
    // this is courtesy — somebody who edits it gets the refusal below, which
    // leaves the filter empty and the rest of the screen working.
    if (!mayListTerminals) {
      setTerminals([]);
      return;
    }

    const controller = new AbortController();
    let live = true;

    collectPages<TerminalResource>((from, limit) =>
      client.request(TerminalsController_list_v1, {
        query: { offset: from, limit },
        signal: controller.signal,
      }),
    )
      .then((all) => {
        if (live) setTerminals(all);
      })
      .catch(() => {
        if (live) setTerminals([]);
      });

    return () => {
      live = false;
      controller.abort();
    };
  }, [client, mayListTerminals]);

  useEffect(() => {
    if (!mayListPeople) {
      setPeople([]);
      return;
    }

    const controller = new AbortController();
    let live = true;

    collectPages<UserResource>((from, limit) =>
      client.request(UsersController_list_v1, {
        query: { offset: from, limit },
        signal: controller.signal,
      }),
    )
      .then((all) => {
        if (live) setPeople(all);
      })
      .catch(() => {
        if (live) setPeople([]);
      });

    return () => {
      live = false;
      controller.abort();
    };
  }, [client, mayListPeople]);

  const outletNames = useMemo(
    () => new Map(outlets.map((outlet) => [outlet.id, outlet.name])),
    [outlets],
  );

  // Keyed by `actorId` and not by `id`. A ticket names the **actor** the acting
  // staff code resolved to, which is a different identifier from the user's own
  // — `UserResource` carries both and `actors.id` is what history is written
  // against. Keying this by `id` would produce a table of empty cells and no
  // error anywhere.
  const personNames = useMemo(
    () => new Map(people.map((person) => [person.actorId, person.displayName])),
    [people],
  );

  const outletItems = useMemo(
    () => [
      { label: 'All outlets', value: ANY },
      ...outlets.map((outlet) => ({ label: outlet.name, value: outlet.id })),
    ],
    [outlets],
  );

  // The prefix first, because it is the part printed on every ticket this device
  // issues (FR-116) and so the part somebody is reading off the artefact in front
  // of them when they come to filter by it.
  const terminalItems = useMemo(
    () => [
      { label: 'All terminals', value: ANY },
      ...terminals.map((terminal) => ({
        label: `${terminal.prefix} — ${terminal.name}`,
        value: terminal.id,
      })),
    ],
    [terminals],
  );

  const personItems = useMemo(
    () => [
      { label: 'Anybody', value: ANY },
      ...people.map((person) => ({ label: person.displayName, value: person.actorId })),
    ],
    [people],
  );

  // The outlet column earns its place only when it can discriminate. Filtered to
  // one outlet it is that outlet's name repeated down the page, which is a column
  // staff learn to stop reading.
  const showOutlet = outletId === ANY;

  // And the person column only where there is a directory to name one from.
  const showPerson = mayListPeople;

  const columns = FIXED_COLUMNS + (showOutlet ? 1 : 0) + (showPerson ? 1 : 0);

  const filtering =
    outletId !== ANY ||
    terminalId !== ANY ||
    actorId !== ANY ||
    kind !== ANY ||
    businessDate !== '';

  return (
    <Screen
      title="Tickets"
      aside={
        <>
          {page !== null && state.kind !== 'failed' && (
            <ScreenFact label="Matching">
              <span className="font-mono numeric">{page.total}</span>{' '}
              {page.total === 1 ? 'ticket' : 'tickets'}
              {businessDate !== '' && <> on {formatBusinessDate(businessDate)}</>}
              {hideVoided && ', voided excluded'}
            </ScreenFact>
          )}

          {/* Not a form and so not `react-hook-form` — the filter rail is
              controls on a list rather than an answer somebody submits, which is
              the same shape the catalogue's filters take. */}
          <div className="space-y-2">
            <Label htmlFor="day-filter" className="text-sm text-muted-foreground">
              Trading day
            </Label>
            {/* The API takes a calendar date in Asia/Kolkata and refuses an
                instant, so the value moving through here is `YYYY-MM-DD` and
                never carries a time. `business-date.ts` owns both conversions
                and says why they are local-midnight rather than UTC. */}
            <DayPicker
              id="day-filter"
              value={day}
              format={formatBusinessDate}
              fromDate={fromCalendarDate}
              toDate={toCalendarDate}
              onValueChange={(next) => {
                changeFilter(() => setDay(next));
              }}
            />

            {!dayIsReal && (
              // The treatment `FormMessage` gives a refusal everywhere else in
              // the app, so a message under a field looks the same wherever it
              // appears.
              <p role="status" className="text-sm text-destructive">
                {BUSINESS_DATE_MALFORMED}
              </p>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="outlet-filter" className="text-sm text-muted-foreground">
              Outlet
            </Label>
            <Select
              value={outletId}
              items={outletItems}
              // `null` is Base UI's "cleared", which these pickers have no
              // affordance for — there is no clear button and no empty option.
              // Read as "not filtering" rather than cast away, so a cleared
              // select can never become an `outletId=null` in the query string.
              onValueChange={(next) => {
                changeFilter(() => setOutletId(next ?? ANY));
              }}
            >
              <SelectTrigger
                id="outlet-filter"
                aria-label="Outlet"
                // Nothing to choose between until the outlets land, and a picker
                // with one option is a control that cannot do anything.
                disabled={outlets.length === 0}
                className="h-10 w-full bg-card"
              >
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectGroup>
                  <SelectItem value={ANY}>All outlets</SelectItem>
                  {outlets.map((outlet) => (
                    <SelectItem key={outlet.id} value={outlet.id}>
                      {outlet.name}
                    </SelectItem>
                  ))}
                </SelectGroup>
              </SelectContent>
            </Select>
          </div>

          {/* Rendered only where there is a device directory to fill it. Hidden
              rather than shown and refused, per docs/ui-conventions.md. */}
          {mayListTerminals && (
            <div className="space-y-2">
              <Label htmlFor="terminal-filter" className="text-sm text-muted-foreground">
                Terminal
              </Label>
              <Select
                value={terminalId}
                items={terminalItems}
                onValueChange={(next) => {
                  changeFilter(() => setTerminalId(next ?? ANY));
                }}
              >
                <SelectTrigger
                  id="terminal-filter"
                  aria-label="Terminal"
                  disabled={terminals.length === 0}
                  className="h-10 w-full bg-card"
                >
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectGroup>
                    <SelectItem value={ANY}>All terminals</SelectItem>
                    {terminals.map((terminal) => (
                      <SelectItem key={terminal.id} value={terminal.id}>
                        {terminal.prefix} — {terminal.name}
                      </SelectItem>
                    ))}
                  </SelectGroup>
                </SelectContent>
              </Select>
            </div>
          )}

          {mayListPeople && (
            <div className="space-y-2">
              <Label htmlFor="person-filter" className="text-sm text-muted-foreground">
                Issued by
              </Label>
              <Select
                value={actorId}
                items={personItems}
                onValueChange={(next) => {
                  changeFilter(() => setActorId(next ?? ANY));
                }}
              >
                <SelectTrigger
                  id="person-filter"
                  aria-label="Issued by"
                  disabled={people.length === 0}
                  className="h-10 w-full bg-card"
                >
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectGroup>
                    <SelectItem value={ANY}>Anybody</SelectItem>
                    {people.map((person) => (
                      <SelectItem key={person.id} value={person.actorId}>
                        {person.displayName}
                      </SelectItem>
                    ))}
                  </SelectGroup>
                </SelectContent>
              </Select>
            </div>
          )}

          <div className="space-y-2">
            <Label htmlFor="kind-filter" className="text-sm text-muted-foreground">
              Kind
            </Label>
            <Select
              value={kind}
              items={[
                { label: 'Everything', value: ANY },
                ...TICKET_ITEM_KINDS.map((option) => ({
                  label: labelForItemKind(option),
                  value: option,
                })),
              ]}
              onValueChange={(next) => {
                changeFilter(() => setKind(next ?? ANY));
              }}
            >
              <SelectTrigger id="kind-filter" aria-label="Kind" className="h-10 w-full bg-card">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectGroup>
                  <SelectItem value={ANY}>Everything</SelectItem>
                  {TICKET_ITEM_KINDS.map((option) => (
                    <SelectItem key={option} value={option}>
                      {labelForItemKind(option)}
                    </SelectItem>
                  ))}
                </SelectGroup>
              </SelectContent>
            </Select>
          </div>

          <div className="flex items-center gap-3">
            {/* Off, and it must stay off. A voided ticket is part of the day's
                record — see the head of this file, and FR-333. */}
            <Switch
              id="hide-voided"
              aria-labelledby="hide-voided-label"
              checked={hideVoided}
              onCheckedChange={(checked) => {
                changeFilter(() => setHideVoided(checked));
              }}
            />
            <Label id="hide-voided-label" htmlFor="hide-voided" className="cursor-pointer text-sm">
              Hide voided tickets
            </Label>
          </div>
        </>
      }
    >
      {/* Above the list and not in the filter rail, because it is not a filter:
          it answers a different question — *this* ticket, the one in somebody's
          hand — and it is the question this screen exists to answer quickly. A
          visitor comes back to the counter with a printed ticket and the number
          on it is all anybody has, so the field for it is the first thing on the
          screen a manager is already on rather than a search page they have to
          find first. See `TicketLookup.tsx`. */}
      <TicketLookup />

      <ScreenTable label="Tickets">
        <TableHeader>
          <TableRow>
            <TableHead className="w-48">Ticket</TableHead>
            <TableHead className="w-56">Sold</TableHead>
            {showOutlet && <TableHead>Outlet</TableHead>}
            {showPerson && <TableHead>Issued by</TableHead>}
            <TableHead className="w-32">Kind</TableHead>
            {/* Amounts right-align, per docs/ui-conventions.md — cash-up is the
                task of comparing down a column. */}
            <TableHead className="w-36 text-right">Amount</TableHead>
          </TableRow>
        </TableHeader>

        {state.kind === 'loading' && <TableSkeleton columns={columns} />}

        {state.kind === 'failed' && (
          <TableNotice columns={columns}>
            <LoadFailed error={state.error} onRetry={reload} />
          </TableNotice>
        )}

        {state.kind === 'loaded' &&
          (state.tickets.length === 0 ? (
            <TableNotice columns={columns}>
              <EmptyState
                title={filtering ? 'Nothing matches these filters' : 'No tickets yet'}
                description={emptyDescription({ filtering, hideVoided })}
              />
            </TableNotice>
          ) : (
            <TableBody>
              {state.tickets.map((ticket) => (
                <TicketRow
                  key={ticket.id}
                  ticket={ticket}
                  outletName={
                    showOutlet ? (outletNames.get(ticket.outletId) ?? UNNAMED) : undefined
                  }
                  personName={showPerson ? (personNames.get(ticket.actorId) ?? UNNAMED) : undefined}
                />
              ))}
            </TableBody>
          ))}
      </ScreenTable>

      {/* Not while the load failed: the notice above already says the list could
          not be read, and a pager under it offers to move through rows that are
          not there. `Pager` draws nothing when everything fits on one page. */}
      {page !== null && state.kind !== 'failed' && (
        <Pager state={page} label="tickets" onOffsetChange={setOffset} />
      )}
    </Screen>
  );
}

/**
 * What stands in for a name the directory could not supply.
 *
 * Reachable in one way only: the outlets, terminals or people read failed, which
 * leaves its filter empty and disabled — so the reason the cell is blank is
 * already visible in the rail beside it. An em dash and not "Unknown", which
 * would claim the server said something it did not.
 */
const UNNAMED = '—';

/**
 * What an empty table should say, which depends on why it is empty.
 *
 * Never a bare "No results". The cases are genuinely different — a park that has
 * not sold anything yet, filters that exclude everything, and filters that
 * exclude everything *because voided tickets are hidden* — and the sentence that
 * helps the first is a dead end for the others.
 *
 * There is no "create the first one" here and there deliberately is not: a
 * ticket is issued at a till against a lease, never from the back office, so a
 * call to action on this screen would point at a control that cannot exist.
 */
function emptyDescription({
  filtering,
  hideVoided,
}: {
  filtering: boolean;
  hideVoided: boolean;
}): string {
  if (filtering) {
    return hideVoided
      ? 'Widen a filter, or turn off Hide voided tickets.'
      : 'Widen the day, the outlet or the kind.';
  }

  if (hideVoided) {
    return 'Turn off Hide voided tickets to see voided ones.';
  }

  return 'Tickets appear here as the tills sync them.';
}

/**
 * One ticket.
 *
 * **A voided ticket is present and marked, never missing** (FR-333). It is
 * quieter than a live one, struck through on the amount and badged in the ticket
 * cell — three marks rather than one, because this is the row somebody is
 * looking for when a day's takings do not add up, and it has to be findable both
 * by scanning down the first column and by reading a single row.
 *
 * It is not coloured `destructive`. Red in this system means the system refused
 * something or is about to; a void is an ordinary correction that already
 * happened, and money that was taken and given back is part of the record rather
 * than an error in it.
 *
 * **`itemRetiredAtIssue` is a quiet badge and never a warning.** It means the
 * item was retired between the till's last catalogue fetch and the server
 * accepting the sale (R-306, FR-334). The ticket is valid, nothing was refused
 * and nobody did anything wrong — it exists so that whoever reconciles the day
 * can find these without reading the catalogue's history, and it is marked
 * `secondary` rather than `outline` so it reads as a note and not as the void
 * beside it.
 *
 * The **row carries no controls**. Voiding a ticket from the back office is
 * `POST /v1/tickets/{id}/void` and belongs to its own ticket; this screen reads.
 */
function TicketRow({
  ticket,
  outletName,
  personName,
}: {
  ticket: TicketResource;

  /** Absent when the outlet column is not drawn. */
  outletName?: string | undefined;

  /** Absent when the "Issued by" column is not drawn. */
  personName?: string | undefined;
}) {
  // Read as a presence and not as a date. `voidedAt` is a real `string | null`
  // now — `ticket-void.ts` records that the annotation gap closed — so a moment
  // *could* be formatted here; a second date down a column of rows is the thing
  // this list already declines to draw, and it is not this change's to add. It
  // is one function rather than the check written out twice because the record
  // screen asks the same question of the same field.
  const voided = isVoided(ticket);

  // And separately, *why* — three states, because `voidReason: null` means no
  // reason was given and never that the ticket stands.
  const reason = readVoidReason(ticket);

  const gap = describeRecordingGap(ticket.occurredAt, ticket.recordedAt);

  // `text-muted-foreground` is safe here — the row sits on `bg-card`, and it is
  // only the muted-on-muted pairing that misses AA.
  const quiet = voided ? '' : 'text-foreground';

  return (
    <TableRow className={voided ? 'text-muted-foreground' : undefined}>
      {/* A ticket number is read aloud off the artefact and typed back in, so it
          gets the mono face and tabular figures. It carries the terminal's own
          prefix (FR-307), which is why there is no separate terminal column —
          `T03-000871` says which device printed it. */}
      <TableCell className="whitespace-normal">
        <div className="flex flex-wrap items-center gap-2">
          {/* The number is the handle, so it is what follows through to the
              record — the same treatment the catalogue gives an amount that
              leads to its price history: a dotted underline, not a column of
              blue links. Named per row, because "T03-000871" repeated down a
              list of links is no help to somebody navigating by them. */}
          <Link
            to={ticketPath(ticket.id)}
            aria-label={`The record for ticket ${ticket.number}`}
            className={`rounded-sm font-mono numeric underline decoration-border decoration-dotted underline-offset-4 outline-none hover:decoration-current focus-visible:ring-3 focus-visible:ring-ring/30 ${quiet}`}
          >
            {ticket.number}
          </Link>

          {voided && <Badge variant="outline">Voided</Badge>}

          {ticket.itemRetiredAtIssue && <Badge variant="secondary">Retired item</Badge>}
        </div>

        {/* Why it was voided, beneath the badge that says it was — grouped with
            the void rather than given a column of its own, because a column
            that is empty on every live row is a column people stop reading, and
            this list is mostly live rows.

            **The null branch is text and never a blank.** A voided row with
            nothing here reads as an answer, and the answer it reads as is "this
            one still stands" — the exact misreading a day's reconciliation
            cannot afford. A till is not asked for a reason; the record screen
            says so at length, and a row says it in three words.

            It wraps rather than clamps. The field is bounded at 280 characters,
            voided rows are the minority, and a truncated reason on the screen
            somebody scans at close is a reason they have to click to finish
            reading — which is the whole cost this line was added to save. */}
        {reason.kind !== 'not-voided' &&
          (reason.kind === 'given' ? (
            <p className="mt-1.5 text-xs text-muted-foreground">{reason.reason}</p>
          ) : (
            // Italic, and it is the one thing distinguishing the two branches at
            // a glance: everything else in this cell is what somebody typed or
            // what a till printed, and this line is the app speaking about what
            // is absent. A scanner who could not tell them apart would read
            // "No reason was given" as a reason somebody gave.
            <p className="mt-1.5 text-xs text-muted-foreground italic">{NO_VOID_REASON_GIVEN}</p>
          ))}
      </TableCell>

      {/* The till's clock: when the visitor actually paid. The server's clock is
          not shown as a second date — it is what the list is ordered by, and
          what a reader wants from it is the *distance*, which is the account of
          an outage. See `ticket-time.ts`. */}
      <TableCell className="whitespace-normal">
        <span className={quiet}>{formatInstant(ticket.occurredAt)}</span>
        {gap !== null && <span className="mt-0.5 block text-xs text-muted-foreground">{gap}</span>}
      </TableCell>

      {outletName !== undefined && (
        <TableCell className={`whitespace-normal ${quiet}`}>{outletName}</TableCell>
      )}

      {personName !== undefined && (
        <TableCell className={`whitespace-normal ${quiet}`}>{personName}</TableCell>
      )}

      <TableCell className={quiet}>{labelForItemKind(ticket.itemKind)}</TableCell>

      {/* Struck through when voided, and still legible: the amount that was taken
          and given back is exactly the number somebody reconciling the day needs
          to see, so it is marked rather than blanked. `formatPaise` is the only
          thing that turns paise into an amount — nothing here divides by 100. */}
      <TableCell className={`text-right font-mono numeric ${quiet}`}>
        <span className={voided ? 'line-through' : undefined}>
          {formatPaise(ticket.amountPaise)}
        </span>
      </TableCell>
    </TableRow>
  );
}
